import {Injectable} from '@angular/core';
import {SeFeApiService} from 'se-fe-api';
import {catchError, forkJoin, map, Observable, of, shareReplay, switchMap, throwError} from 'rxjs';
import {ApiResponse} from 'se-resource-types/dist/lib/common';
import {PaginationParameters} from '../models/pagination-parameters';
import {HttpParams} from '@angular/common/http';
import {Sale} from './models/sale';
import {CheckoutSummaryGroup} from './models/checkout-summary-group';
import {SaleItem} from './models/sale-item';
import {Register} from './models/register';

@Injectable()
export class OrderApiService {
  private readonly checkoutSummaryGroupCache = new Map<string, Observable<ApiResponse<CheckoutSummaryGroup>>>();

  constructor(private apiService: SeFeApiService) {}

  list(pagination: PaginationParameters): Observable<ApiResponse<Sale[]>> {
    let params = new HttpParams().set('per_page', pagination.limit).set('page', pagination.currentPage);
    if (pagination.search) {
      params = params.set('search', pagination.search);
    }

    return this.apiService
      .get<Sale[]>('/sales/mine', {
        params,
        headers: {
          Accept: 'application/json',
        },
        withCredentials: true,
      })
      .pipe(switchMap(response => this.enrichSalesWithCheckoutSummaryItems(response)));
  }

  get(saleNumber: string): Observable<ApiResponse<Sale>> {
    const params = {sale_number: saleNumber, use_order_history_serializer: 'true'};
    return this.apiService
      .get<Sale>('/sales/show', {
        params,
        headers: {
          Accept: 'application/json',
        },
        withCredentials: true,
      })
      .pipe(
        switchMap(response =>
          this.enrichSaleWithCheckoutSummaryItems(response.result).pipe(map(order => ({...response, result: order})))
        )
      );
  }

  private enrichSalesWithCheckoutSummaryItems(response: ApiResponse<Sale[]>): Observable<ApiResponse<Sale[]>> {
    const sales = response.result ?? [];
    if (sales.length === 0) {
      return of(response);
    }

    return forkJoin(sales.map(sale => this.enrichSaleWithCheckoutSummaryItems(sale))).pipe(
      map(enrichedSales => ({...response, result: enrichedSales}))
    );
  }

  private enrichSaleWithCheckoutSummaryItems(sale: Sale): Observable<Sale> {
    if (!sale.sale_number || !this.shouldEnrichFromCheckoutSummary(sale)) {
      return of(sale);
    }

    return this.getCheckoutSummaryGroup(sale.sale_number).pipe(
      map(response => this.mergeSaleItems(sale, response.result)),
      catchError(() => of(sale))
    );
  }

  private getCheckoutSummaryGroup(saleNumber: string): Observable<ApiResponse<CheckoutSummaryGroup>> {
    const cached = this.checkoutSummaryGroupCache.get(saleNumber);
    if (cached) {
      return cached;
    }

    const request$ = this.apiService
      .get<CheckoutSummaryGroup>('/cart/checkout_summary_group', {
        params: {sale_number: saleNumber},
        headers: {
          Accept: 'application/json',
        },
        withCredentials: true,
      })
      .pipe(
        catchError(error => {
          this.checkoutSummaryGroupCache.delete(saleNumber);
          return throwError(() => error);
        }),
        shareReplay(1)
      );

    this.checkoutSummaryGroupCache.set(saleNumber, request$);
    return request$;
  }

  private mergeSaleItems(sale: Sale, checkoutSummaryGroup: CheckoutSummaryGroup): Sale {
    const checkoutItems = checkoutSummaryGroup.checkout_summaries?.[0]?.sale_totals?.items;
    if (!Array.isArray(checkoutItems) || checkoutItems.length === 0) {
      return sale;
    }

    const originalItemsById = new Map((sale.items ?? []).map(item => [item.id, item]));
    const mergedCheckoutItems = checkoutItems.map(checkoutItem => {
      const originalItem = originalItemsById.get(checkoutItem.id);
      if (!originalItem) {
        return this.normalizeCheckoutItem(checkoutItem);
      }

      return {
        ...originalItem,
        ...checkoutItem,
        metadata: {
          ...originalItem.metadata,
          ...checkoutItem.metadata,
          SeCommerceApi: {
            ...originalItem.metadata?.SeCommerceApi,
            ...checkoutItem.metadata?.SeCommerceApi,
          },
        },
      };
    });

    const mergedItemIds = new Set(mergedCheckoutItems.map(item => item.id));
    const untouchedItems = (sale.items ?? []).filter(item => !mergedItemIds.has(item.id));

    return {
      ...sale,
      items: [...mergedCheckoutItems, ...untouchedItems],
    };
  }

  private normalizeCheckoutItem(item: SaleItem): SaleItem {
    return {
      ...item,
      item_name: item.item_name || item.full_name || item.name,
      item_variation_name: item.item_variation_name || '',
      promo_item: item.promo_item ?? false,
    };
  }

  private shouldEnrichFromCheckoutSummary(sale: Sale): boolean {
    const items = sale.items ?? [];
    if (items.length === 0) {
      return false;
    }

    if (items.some(item => this.hasEmbeddedBundleMetadata(item))) {
      return false;
    }

    if (items.some(item => this.hasRawBundleFields(item))) {
      return true;
    }

    return (
      this.hasBundleSummaryRegister(sale.registers ?? []) ||
      items.some(item => this.looksLikeNumberedBundleItem(item)) ||
      this.hasRepeatedItemNames(items)
    );
  }

  private hasEmbeddedBundleMetadata(item: SaleItem): boolean {
    const metadata = item.metadata?.SeCommerceApi;
    return Boolean(metadata?.bundle_instance_id || metadata?.bundle_name || metadata?.bundle_id);
  }

  private hasRawBundleFields(item: SaleItem): boolean {
    return Boolean(item.bundle_instance_id || item.bundle_name || item.bundle_id);
  }

  private hasBundleSummaryRegister(registers: Register[]): boolean {
    return registers.some(
      register => register.register_type === 'sale_item' && register.line_level !== 'sale_item_line'
    );
  }

  private looksLikeNumberedBundleItem(item: SaleItem): boolean {
    const source = item.item_name || item.name || item.full_name || '';
    return /^\s*#\d+\s+.+?\s+Product\b/i.test(source);
  }

  private hasRepeatedItemNames(items: SaleItem[]): boolean {
    const counts = items.reduce((map, item) => {
      const key = (item.item_name || item.name || item.full_name || '').trim().toLowerCase();
      if (!key) {
        return map;
      }
      map.set(key, (map.get(key) ?? 0) + 1);
      return map;
    }, new Map<string, number>());

    return [...counts.values()].filter(count => count > 1).length >= 2;
  }
}
